import { useState, useEffect } from 'react';
import { apiGet } from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, 
  Download,
  FileSpreadsheet,
  FileText,
  Search,
  Filter,
  Users,
  Calendar,
  Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const ExportSystemPage = () => {
  const navigate = useNavigate();
  
  const [suppliers, setSuppliers] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Export options
  const [exportType, setExportType] = useState('workers');
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exportFormat, setExportFormat] = useState('excel');

  // Load data
  const loadData = async () => {
    try {
      const [suppliersData, workersData] = await Promise.all([
        apiGet('/api/suppliers'),
        apiGet('/api/cases')
      ]);
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      setWorkers(Array.isArray(workersData) ? workersData : []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      if (selectedSupplier !== 'all') params.append('supplier_id', selectedSupplier);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      params.append('format', exportFormat);

      // Get export endpoint based on type
      let endpoint = '/api/export/';
      switch (exportType) {
        case 'workers':
          endpoint += 'workers';
          break;
        case 'visas':
          endpoint += 'visas';
          break;
        case 'transactions':
          endpoint += 'transactions';
          break;
        case 'tickets':
          endpoint += 'tickets';
          break;
        default:
          endpoint += 'workers';
      }

      // For now, generate a client-side export
      let dataToExport = [];
      let filename = '';

      if (exportType === 'workers') {
        dataToExport = workers.filter(w => 
          selectedSupplier === 'all' || w.supplierId === selectedSupplier
        );
        filename = `workers_export_${new Date().toISOString().split('T')[0]}`;
      } else {
        toast.info('Export functionality for this type is coming soon!');
        setExporting(false);
        return;
      }

      if (dataToExport.length === 0) {
        toast.error('No data to export');
        setExporting(false);
        return;
      }

      // Generate CSV
      if (exportFormat === 'csv' || exportFormat === 'excel') {
        const headers = ['Worker Name', 'Nationality', 'Status', 'Supplier', 'Contract Amount', 'Remaining', 'Arrival Date'];
        const rows = dataToExport.map(w => [
          w.workerName || '',
          w.nationality || '',
          w.status || '',
          w.supplierName || '',
          w.contractAmount || 0,
          w.remaining || 0,
          w.arrivalDate || ''
        ]);

        const csvContent = [headers, ...rows]
          .map(row => row.map(cell => `"${cell}"`).join(','))
          .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${filename}.csv`;
        link.click();
        
        toast.success(`Exported ${dataToExport.length} records successfully!`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Stats
  const stats = {
    totalWorkers: workers.length,
    totalSuppliers: suppliers.length,
    abroad: workers.filter(w => w.status === 'abroad').length,
    arrived: workers.filter(w => w.status === 'arrived').length
  };

  return (
    <div className="space-y-6" data-testid="export-system-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/dashboard')}
          className="h-11 w-11 rounded-full bg-[#2d3c59] hover:bg-[#3e527a]"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-3xl md:text-4xl font-bold text-white italic">Export System</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="text-center p-3 bg-[#1A2D47] rounded-xl">
          <p className="text-xl font-bold text-white">{stats.totalWorkers}</p>
          <p className="text-[10px] text-white/50">Total Workers</p>
        </div>
        <div className="text-center p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl">
          <p className="text-xl font-bold text-blue-400">{stats.totalSuppliers}</p>
          <p className="text-[10px] text-blue-400/70">Suppliers</p>
        </div>
        <div className="text-center p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
          <p className="text-xl font-bold text-emerald-400">{stats.arrived}</p>
          <p className="text-[10px] text-emerald-400/70">Arrived</p>
        </div>
        <div className="text-center p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <p className="text-xl font-bold text-amber-400">{stats.abroad}</p>
          <p className="text-[10px] text-amber-400/70">Abroad</p>
        </div>
      </div>

      {/* Export Options Card */}
      <Card className="bg-gradient-to-br from-[#1A2D47] to-[#0F2540] border-[#004B93]/30 rounded-2xl">
        <CardContent className="p-6 space-y-6">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Download className="h-5 w-5 text-blue-400" />
            Export Data
          </h2>

          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 rounded-xl bg-[#0F2540]" />
              <Skeleton className="h-10 rounded-xl bg-[#0F2540]" />
              <Skeleton className="h-10 rounded-xl bg-[#0F2540]" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                {/* Export Type */}
                <div className="space-y-2">
                  <Label className="text-white/70">Export Type</Label>
                  <Select value={exportType} onValueChange={setExportType}>
                    <SelectTrigger className="bg-[#0F2540] border-[#004B93]/30 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A2D47] border-[#004B93]/30">
                      <SelectItem value="workers">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Workers Data
                        </div>
                      </SelectItem>
                      <SelectItem value="visas">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Visas Data
                        </div>
                      </SelectItem>
                      <SelectItem value="transactions">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="h-4 w-4" />
                          Transactions
                        </div>
                      </SelectItem>
                      <SelectItem value="tickets">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Flight Tickets
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Supplier Filter */}
                <div className="space-y-2">
                  <Label className="text-white/70">Supplier Filter</Label>
                  <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                    <SelectTrigger className="bg-[#0F2540] border-[#004B93]/30 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A2D47] border-[#004B93]/30">
                      <SelectItem value="all">All Suppliers</SelectItem>
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Export Format */}
                <div className="space-y-2">
                  <Label className="text-white/70">Export Format</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant={exportFormat === 'excel' ? 'default' : 'outline'}
                      onClick={() => setExportFormat('excel')}
                      className={exportFormat === 'excel' ? 'bg-emerald-500' : 'border-white/20'}
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Excel/CSV
                    </Button>
                    <Button
                      variant={exportFormat === 'pdf' ? 'default' : 'outline'}
                      onClick={() => setExportFormat('pdf')}
                      className={exportFormat === 'pdf' ? 'bg-red-500' : 'border-white/20'}
                      disabled
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Date Range */}
                <div className="space-y-2">
                  <Label className="text-white/70">Date Range (Optional)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-white/50">From</Label>
                      <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="bg-[#0F2540] border-[#004B93]/30 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-white/50">To</Label>
                      <Input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="bg-[#0F2540] border-[#004B93]/30 text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview Stats */}
                <div className="bg-[#0F2540] rounded-xl p-4 space-y-2">
                  <p className="text-sm font-semibold text-white/70">Export Preview</p>
                  <div className="text-2xl font-bold text-white">
                    {exportType === 'workers' 
                      ? (selectedSupplier === 'all' ? workers.length : workers.filter(w => w.supplierId === selectedSupplier).length)
                      : '—'
                    } records
                  </div>
                  <p className="text-xs text-white/50">
                    {exportType === 'workers' && 'Worker records ready for export'}
                    {exportType === 'visas' && 'Visa records ready for export'}
                    {exportType === 'transactions' && 'Transaction records ready for export'}
                    {exportType === 'tickets' && 'Ticket records ready for export'}
                  </p>
                </div>

                {/* Export Button */}
                <Button
                  onClick={handleExport}
                  disabled={exporting || loading}
                  className="w-full h-12 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold"
                >
                  {exporting ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="h-5 w-5 mr-2" />
                      Export Data
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Export Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card 
          className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/30 rounded-xl cursor-pointer hover:border-emerald-500/50 transition-all"
          onClick={() => {
            setExportType('workers');
            setSelectedSupplier('all');
            setExportFormat('excel');
          }}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Users className="h-6 w-6 text-emerald-400" />
            </div>
            <div>
              <p className="font-semibold text-white">All Workers</p>
              <p className="text-xs text-white/50">{workers.length} records</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/30 rounded-xl cursor-pointer hover:border-blue-500/50 transition-all"
          onClick={() => {
            setExportType('visas');
            setExportFormat('excel');
          }}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <FileText className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <p className="font-semibold text-white">All Visas</p>
              <p className="text-xs text-white/50">Export visa data</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/30 rounded-xl cursor-pointer hover:border-amber-500/50 transition-all"
          onClick={() => {
            setExportType('transactions');
            setExportFormat('excel');
          }}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <FileSpreadsheet className="h-6 w-6 text-amber-400" />
            </div>
            <div>
              <p className="font-semibold text-white">Transactions</p>
              <p className="text-xs text-white/50">Financial records</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ExportSystemPage;
